/** Universal Dispute Ledger — available to every trade org variant. */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function DisputeLedgerPanel({ org, quorum, accent = "#F5B82E" }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen]   = useState(false);
  const unlocked = quorum?.full_features_unlocked;

  const refresh = async () => {
    if (!unlocked) return;
    try {
      const { data } = await api.get("/disputes", { params: { org_id: org.id, limit: 20 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.warn("[DisputeLedgerPanel] load skipped:", err?.message);
    }
  };
  useEffect(() => { void refresh(); }, [unlocked, org.id]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="dispute-ledger-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: `${accent}20`, color: accent }}>
            <Gavel size={16} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Dispute Ledger</div>
            <div className="font-display text-xl uppercase tracking-tight" data-testid="disputes-total">
              {total.toLocaleString()} disputes on record
            </div>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!unlocked}
                    className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                    style={unlocked ? { background: accent, color: "#0b0b0d" } : {}}
                    data-testid="file-dispute-btn">
              <Plus size={12} className="mr-1.5" /> File Dispute
            </Button>
          </DialogTrigger>
          <FileDisputeDialog orgId={org.id} onDone={() => { setOpen(false); refresh(); }} />
        </Dialog>
      </div>
      {!unlocked && (
        <div className="mt-4 flex items-center gap-2 text-[11px] text-amber-300">
          <ShieldAlert size={12} /> Feature unlocks after quorum + KYC.
        </div>
      )}
      {unlocked && items.length === 0 && (
        <div className="mt-4 text-sm text-white/55">No open disputes.</div>
      )}
      {unlocked && items.length > 0 && (
        <ul className="mt-4 divide-y divide-white/5" data-testid="disputes-list">
          {items.map((d) => (
            <li key={d.id} className="py-2.5 flex items-center gap-3" data-testid={`dispute-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">
                  {d.claimant_name} <span className="text-white/40">vs</span> {d.respondent_name}
                </div>
                <div className="text-[11px] text-white/55">
                  {d.dispute_type} · filed {new Date(d.opened_at).toLocaleDateString()}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase ${
                d.stage === "resolved" || d.stage === "closed"
                  ? "bg-emerald-500/10 text-emerald-300"
                  : d.stage === "hearing"
                    ? "bg-red-500/10 text-red-300"
                    : "bg-amber-500/10 text-amber-300"
              }`}>{d.stage}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileDisputeDialog({ orgId, onDone }) {
  const [claimant, setClaimant] = useState("");
  const [respondent, setRespondent] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("payment");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/disputes", {
        org_id: orgId,
        claimant_name: claimant,
        respondent_name: respondent,
        description,
        dispute_type: type,
      });
      toast.success("Dispute filed");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="file-dispute-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">File a Dispute</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Committee handles the stage transitions after filing.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Claimant *</Label>
            <Input value={claimant} onChange={(e) => setClaimant(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="claimant-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Respondent *</Label>
            <Input value={respondent} onChange={(e) => setRespondent(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="respondent-input" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Type</Label>
          <select value={type} onChange={(e) => setType(e.target.value)}
                  className="mt-2 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-white text-sm"
                  data-testid="dispute-type-select">
            {["payment","contract_breach","credit","title","conduct","harassment","safety","other"].map(k =>
              <option key={k} value={k}>{k.replace("_"," ")}</option>
            )}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Description *</Label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="mt-2 w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-white text-sm"
                    data-testid="dispute-description-input" />
        </div>
        <Button onClick={submit} disabled={!claimant || !respondent || description.length < 4 || busy}
                className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="submit-dispute-btn">
          {busy ? "Filing…" : "File Dispute"}
        </Button>
      </div>
    </DialogContent>
  );
}
